import { useState } from 'react';
import Link from 'next/link';
import { useTranslate } from '@tolgee/react';
import { TournamentCategoryItemFragment } from '@amf/shared/types/graphql';
import { TournamentGroundType, TournamentPlayType } from '@amf/shared/data/Tournament';
import Button from '@amf/shared/components/button/Button';

import TournamentForm, { TournamentFormData } from 'components/tournament/TournamentForm';
import { AppLinks } from 'utils/links';

interface TournamentCreateProps {
  categories: TournamentCategoryItemFragment[];
}

const defaultValues: TournamentFormData = {
  givenName: '',
  familyName: '',
  region: 'pha',
  category: '',
  city: '',
  dateTo: '',
  dateFrom: '',
  fee: 0,
  name: '',
  year: '',
  description: '',
  email: '',
  phone: null,
  link: null,
  latitude: null,
  longitude: null,
  groundType: TournamentGroundType.ARTIFICIAL_GRASS,
  playType: TournamentPlayType.FIVE_PLUS_ONE,
};

export default function TournamentCreate({ categories }: TournamentCreateProps) {
  const { t } = useTranslate(['malyfotbal', 'general']);
  const [error, setError] = useState<Error | undefined>(undefined);
  const [success, setSuccess] = useState(false);

  const saveTournament = async (data: TournamentFormData) => {
    setError(undefined);
    setSuccess(false);

    const response = await fetch('/api/tournament', {
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'POST',
    });

    const result = (await response.json()) as {
      success?: boolean;
      error?: string;
    };

    if (result.error) {
      setError(new Error(result.error));
      return;
    }

    if (result.success) {
      setSuccess(true);
    }
  };

  if (categories.length === 0) {
    return null;
  }

  if (success) {
    return (
      <div className='TournamentCreatePage__success'>
        <h2 className='TournamentCreatePage__successTitle'>
          {t('tournaments.create.success.title')}
        </h2>
        <p className='TournamentCreatePage__successMessage'>
          {t('tournaments.create.success.message')}
        </p>
        <Link href={AppLinks.homepage}>
          <a>
            <Button variant='blue-outline'>{t('tournaments.create.success.action')}</Button>
          </a>
        </Link>
      </div>
    );
  }

  return (
    <>
      {error && <p>{error}</p>}
      <TournamentForm
        categories={categories}
        onSubmit={saveTournament}
        values={{
          ...defaultValues,
          category: categories[0].id,
        }}
      />
    </>
  );
}
